import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
    exportFunnel,
    importFunnelNodes,
    importChatfoodFlow,
    type ImportChatfoodResponse,
} from '@/services/api/funnel';
import { Copy, Download, Upload, Check, AlertCircle, FileJson } from 'lucide-react';
import type { Funnel, FunnelExportData } from '@/types/funnel';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type ImportFormat = 'native' | 'chatfood';

interface ExportImportModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Se fornecido, abre no modo export com os dados desse funnel */
    exportFunnel?: Funnel | null;
    /** Lista de funnels para escolher o destino no import */
    funnels: Funnel[];
}

export default function ExportImportModal({
    open,
    onOpenChange,
    exportFunnel: exportFunnelProp,
    funnels,
}: ExportImportModalProps) {
    const [mode, setMode] = useState<'export' | 'import'>('export');
    const [importFormat, setImportFormat] = useState<ImportFormat>('native');
    const [json, setJson] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [targetFunnelId, setTargetFunnelId] = useState<number | ''>('');
    const [clearExisting, setClearExisting] = useState(true);
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [newFunnelName, setNewFunnelName] = useState('');
    const [chatfoodResult, setChatfoodResult] = useState<ImportChatfoodResponse | null>(null);

    const { toast } = useToast();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // Reset state when modal opens
    useEffect(() => {
        if (!open) return;

        if (exportFunnelProp) {
            setMode('export');
            setJson('');
            setLoading(true);
            exportFunnel(exportFunnelProp.id)
                .then((data) => {
                    setJson(JSON.stringify(data, null, 2));
                })
                .catch((err) => {
                    toast({
                        variant: 'destructive',
                        title: 'Erro ao exportar',
                        description: err.response?.data?.detail || 'Erro ao exportar funil.',
                    });
                })
                .finally(() => setLoading(false));
        } else {
            setMode('import');
            setImportFormat('native');
            setJson('');
            setTargetFunnelId('');
            setClearExisting(true);
            setJsonError(null);
            setNewFunnelName('');
            setChatfoodResult(null);
        }

        setCopied(false);
    }, [open, exportFunnelProp]);

    // Validate JSON on change (import mode) — formato depende do importFormat
    useEffect(() => {
        if (mode !== 'import' || !json.trim()) {
            setJsonError(null);
            return;
        }
        try {
            const parsed = JSON.parse(json);
            if (importFormat === 'native') {
                if (!parsed.nodes || !Array.isArray(parsed.nodes)) {
                    setJsonError('JSON deve conter um array "nodes"');
                } else {
                    setJsonError(null);
                }
            } else {
                // ChatFood: deve ter pelo menos WELCOME
                if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                    setJsonError('JSON ChatFood inválido (deve ser objeto com routes)');
                } else if (!parsed.WELCOME) {
                    setJsonError('JSON ChatFood deve ter chave "WELCOME"');
                } else {
                    setJsonError(null);
                }
            }
        } catch {
            setJsonError('JSON inválido');
        }
    }, [json, mode, importFormat]);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(json);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast({ title: 'JSON copiado!' });
    };

    const importMutation = useMutation({
        mutationFn: importFunnelNodes,
        onSuccess: () => {
            toast({ title: 'Nodes importados com sucesso!' });
            queryClient.invalidateQueries({ queryKey: ['funnels'] });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onOpenChange(false);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao importar',
                description: error.response?.data?.detail || 'Erro ao importar nodes.',
            });
        },
    });

    const chatfoodMutation = useMutation({
        mutationFn: importChatfoodFlow,
        onSuccess: (data) => {
            setChatfoodResult(data);
            queryClient.invalidateQueries({ queryKey: ['funnels'] });
            toast({
                title: 'Funnel criado!',
                description: `#${data.funnel_id}: ${data.nodes_created} nodes, ${data.transitions_created} transitions.`,
            });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao importar ChatFood',
                description: error.response?.data?.detail || 'Erro ao importar flow.',
            });
        },
    });

    const handleImport = () => {
        if (jsonError) return;

        if (importFormat === 'native') {
            if (!targetFunnelId) return;
            try {
                const parsed: FunnelExportData = JSON.parse(json);
                importMutation.mutate({
                    funnel_id: targetFunnelId as number,
                    clear_existing: clearExisting,
                    nodes: parsed.nodes,
                    transitions: parsed.transitions || [],
                });
            } catch {
                setJsonError('JSON inválido');
            }
            return;
        }

        // ChatFood
        if (!newFunnelName.trim()) {
            setJsonError('Informe o nome do funnel novo');
            return;
        }
        try {
            const parsed = JSON.parse(json);
            chatfoodMutation.mutate({
                name: newFunnelName.trim(),
                flow: parsed,
            });
        } catch {
            setJsonError('JSON inválido');
        }
    };

    const targetFunnel = funnels.find((f) => f.id === targetFunnelId);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-lg font-bold text-[#0f172a]">
                        {mode === 'export' ? 'Exportar Funil' : 'Importar Nodes'}
                    </DialogTitle>
                    <p className="text-sm text-gray-500 mt-1">
                        {mode === 'export'
                            ? 'Copie o JSON abaixo e cole em outro funil para duplicar os nodes.'
                            : 'Cole o JSON exportado de outro funil para importar os nodes.'}
                    </p>
                </DialogHeader>

                {/* Mode tabs */}
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                        onClick={() => setMode('export')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            mode === 'export'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Download className="w-4 h-4" />
                        Exportar
                    </button>
                    <button
                        onClick={() => setMode('import')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            mode === 'import'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Upload className="w-4 h-4" />
                        Importar
                    </button>
                </div>

                {/* Export: funnel selector (when switching to export without a pre-selected funnel) */}
                {mode === 'export' && !exportFunnelProp && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Selecione o funil para exportar
                        </label>
                        <select
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={targetFunnelId}
                            onChange={(e) => {
                                const id = Number(e.target.value);
                                setTargetFunnelId(id);
                                if (id) {
                                    setLoading(true);
                                    exportFunnel(id)
                                        .then((data) => setJson(JSON.stringify(data, null, 2)))
                                        .catch(() => toast({ variant: 'destructive', title: 'Erro ao exportar' }))
                                        .finally(() => setLoading(false));
                                }
                            }}
                        >
                            <option value="">Selecione...</option>
                            {funnels.map((f) => (
                                <option key={f.id} value={f.id}>
                                    {f.nome} (#{f.id})
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Import: formato selector + campos por formato */}
                {mode === 'import' && !chatfoodResult && (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Formato do JSON
                            </label>
                            <select
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={importFormat}
                                onChange={(e) => {
                                    setImportFormat(e.target.value as ImportFormat);
                                    setJsonError(null);
                                }}
                                disabled={importMutation.isPending || chatfoodMutation.isPending}
                            >
                                <option value="native">Nosso (exportado de outro funnel)</option>
                                <option value="chatfood">ChatFood (cria funnel novo)</option>
                            </select>
                        </div>

                        {importFormat === 'native' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Funil destino
                                    </label>
                                    <select
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={targetFunnelId}
                                        onChange={(e) => setTargetFunnelId(Number(e.target.value) || '')}
                                    >
                                        <option value="">Selecione o funil destino...</option>
                                        {funnels.map((f) => (
                                            <option key={f.id} value={f.id}>
                                                {f.nome} (#{f.id}) {f.is_active ? '' : '- Inativo'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={clearExisting}
                                        onChange={(e) => setClearExisting(e.target.checked)}
                                        className="rounded border-gray-300"
                                    />
                                    Limpar nodes existentes antes de importar
                                </label>
                                {targetFunnel?.is_active && (
                                    <div className="flex items-center gap-2 text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
                                        <AlertCircle className="w-4 h-4 shrink-0" />
                                        Este funil está ativo. Recomendamos desativá-lo antes de importar.
                                    </div>
                                )}
                            </>
                        )}

                        {importFormat === 'chatfood' && (
                            <div className="space-y-2">
                                <div>
                                    <Label htmlFor="cf-name">Nome do funnel novo</Label>
                                    <Input
                                        id="cf-name"
                                        value={newFunnelName}
                                        onChange={(e) => setNewFunnelName(e.target.value)}
                                        placeholder="Ex: Onboard Shein - Importado ChatFood"
                                        disabled={chatfoodMutation.isPending}
                                    />
                                </div>
                                <div className="flex items-start gap-2 p-3 rounded-md bg-blue-50 border border-blue-200 text-blue-900 text-xs leading-relaxed">
                                    <FileJson className="w-4 h-4 mt-0.5 shrink-0" />
                                    <div className="flex flex-col gap-1">
                                        <span>Imagens do ChatFood serão baixadas e republicadas no nosso storage.</span>
                                        <span><code className="bg-blue-100 px-1 rounded">typing</code> vira <code className="bg-blue-100 px-1 rounded">delay_seconds</code> no próximo node.</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ChatFood — tela de resultado pós-import */}
                {mode === 'import' && chatfoodResult && (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 p-3 rounded-md bg-green-50 border border-green-200 text-green-800">
                            <Check className="w-5 h-5" />
                            <div className="text-sm">
                                <strong>Funnel #{chatfoodResult.funnel_id}</strong> criado com sucesso.
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="p-3 rounded-md bg-gray-50">
                                <div className="text-2xl font-bold">{chatfoodResult.nodes_created}</div>
                                <div className="text-xs text-gray-500">nodes</div>
                            </div>
                            <div className="p-3 rounded-md bg-gray-50">
                                <div className="text-2xl font-bold">{chatfoodResult.transitions_created}</div>
                                <div className="text-xs text-gray-500">transitions</div>
                            </div>
                            <div className="p-3 rounded-md bg-gray-50">
                                <div className="text-2xl font-bold">{chatfoodResult.images_downloaded}</div>
                                <div className="text-xs text-gray-500">imagens</div>
                            </div>
                        </div>
                        {chatfoodResult.warnings.length > 0 && (
                            <div className="p-3 rounded-md bg-yellow-50 border border-yellow-200">
                                <div className="flex items-center gap-2 mb-2 text-yellow-800">
                                    <AlertCircle className="w-4 h-4" />
                                    <strong className="text-sm">
                                        {chatfoodResult.warnings.length} aviso(s)
                                    </strong>
                                </div>
                                <ul className="text-xs text-yellow-700 space-y-1 max-h-32 overflow-y-auto">
                                    {chatfoodResult.warnings.map((w, i) => (
                                        <li key={i}>• {w}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* JSON editor — escondido na tela de resultado ChatFood */}
                {!(mode === 'import' && chatfoodResult) && (
                <div className="flex-1 min-h-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f172a]" />
                        </div>
                    ) : (
                        <div className="relative">
                            <textarea
                                value={json}
                                onChange={(e) => setJson(e.target.value)}
                                readOnly={mode === 'export'}
                                placeholder={mode === 'import' ? 'Cole o JSON exportado aqui...' : ''}
                                className={`w-full h-80 font-mono text-xs border rounded-lg p-4 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    jsonError
                                        ? 'border-red-300 bg-red-50/50'
                                        : 'border-gray-200 bg-gray-50'
                                } ${mode === 'export' ? 'cursor-default' : ''}`}
                                spellCheck={false}
                            />
                            {mode === 'export' && json && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    className="absolute top-2 right-2 gap-1.5"
                                >
                                    {copied ? (
                                        <>
                                            <Check className="w-3.5 h-3.5 text-green-500" />
                                            Copiado
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="w-3.5 h-3.5" />
                                            Copiar
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                    )}
                    {jsonError && (
                        <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" />
                            {jsonError}
                        </p>
                    )}
                </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Fechar
                    </Button>
                    {mode === 'import' && !chatfoodResult && (
                        <Button
                            onClick={handleImport}
                            disabled={
                                !json.trim() ||
                                !!jsonError ||
                                importMutation.isPending ||
                                chatfoodMutation.isPending ||
                                (importFormat === 'native' && !targetFunnelId) ||
                                (importFormat === 'chatfood' && !newFunnelName.trim())
                            }
                        >
                            {(importMutation.isPending || chatfoodMutation.isPending) ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                            ) : (
                                <Upload className="w-4 h-4 mr-2" />
                            )}
                            {importFormat === 'native' ? 'Importar Nodes' : 'Importar ChatFood'}
                        </Button>
                    )}
                    {mode === 'import' && chatfoodResult && (
                        <Button
                            onClick={() => {
                                onOpenChange(false);
                                navigate(`/meta/flow/${chatfoodResult.funnel_id}`);
                            }}
                        >
                            Abrir no Builder
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
